import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';
import {
  HandleAccessError,
  HandleMessageAndParameters,
  HandleGenericError,
  HandleCodeAndParameters,
  HandleCodeAndMessage,
} from '../state/global-error.actions';

interface Error {
  resource: string;
  missingPrivileges: string[];
}

interface ApiError {
  message: string;
  code: string;
  parameters: string[];
  stackTrace: string;
  errors?: { [key: string]: string[] };
  title?: string;
}

const MESSAGE_DISPLAY_DURATION = 10000;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private store: Store,
    private oidcSecurityService: OidcSecurityService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        if (response.status === HttpStatusCode.Forbidden) {
          this.handleAccessError(response);
        } else if (response.status === HttpStatusCode.BadRequest) {
          this.handleBadRequest(response);
        } else if (response.status === HttpStatusCode.NotFound) {
          this.handleBadRequest(response);
        } else if (response.status === HttpStatusCode.Unauthorized) {
          this.handleUnauthorizedError();
        } else if (response.status === HttpStatusCode.ImATeapot) {
          window.location.href = environment.authUrl;
        } else {
          this.store.dispatch(new HandleGenericError());
        }
        return throwError(response);
      })
    );
  }

  private handleBadRequest(response: HttpErrorResponse): void {
    const { error } = response;
    if (error instanceof Blob) {
      error.text().then((text: string) => {
        const errorJson = JSON.parse(text);
        this.displayError(errorJson);
      });
    } else {
      this.displayError(error);
    }
  }

  private displayError(error: ApiError): void {
    if (!error) {
      this.store.dispatch(new HandleGenericError());
      return;
    }
    const { code, message } = error;
    if (code && message && error?.parameters?.length > 0) {
      this.store.dispatch(new HandleMessageAndParameters(error, MESSAGE_DISPLAY_DURATION));
    } else if (code && message) {
      this.store.dispatch(new HandleCodeAndMessage(error, MESSAGE_DISPLAY_DURATION));
    } else if (code) {
      this.store.dispatch(new HandleCodeAndParameters(error));
    } else if (error?.errors) {
      Object.values(error.errors).forEach((e: string[]) =>
        e.forEach((errorMessage) =>
          this.store.dispatch(new HandleGenericError(errorMessage, MESSAGE_DISPLAY_DURATION))
        )
      );
    } else {
      this.store.dispatch(
        new HandleGenericError(message || error?.title || (error as unknown as string))
      );
    }
  }

  private handleAccessError(response: HttpErrorResponse): void {
    const { errors } = response.error;
    if (Array.isArray(errors)) {
      this.store.dispatch(
        new HandleAccessError({
          message: 'missingPrivileges',
          parameters: {
            resource: errors.map((error: Error) => error?.resource),
            privileges: errors.map((error: Error) => {
              const missingPrivileges = error?.missingPrivileges;
              if (missingPrivileges) {
                return missingPrivileges.join(', ');
              }
              return [];
            }),
          },
        })
      );
    }
  }

  private handleUnauthorizedError(): void {
    this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        window.location.reload();
      }
    });
  }
}
